import axios from 'axios'

// Standalone axios instance for cart — avoids circular dependency
// that occurs when importing { api } from './axios' through the barrel index
const cartAxios = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api',
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
})

// Attach auth token to every request
cartAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken')
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

export const cartAPI = {
  getCart: async () => {
    const response = await cartAxios.get('/customer/cart')
    return response.data.data
  },

  addToCart: async ({ productId, quantity, variationId, addonProductAddOnId, addonVariationId }) => {
    const payload = { productId, quantity }
    if (variationId != null) payload.variationId = variationId
    if (addonProductAddOnId != null) payload.addonProductAddOnId = addonProductAddOnId
    if (addonVariationId != null) payload.addonVariationId = addonVariationId
    const response = await cartAxios.post('/customer/cart/items', payload)
    return response.data.data
  },

  updateCartItem: async (itemId, quantity) => {
    const response = await cartAxios.put(`/customer/cart/items/${itemId}`, { quantity })
    return response.data.data
  },

  removeFromCart: async (itemId) => {
    const response = await cartAxios.delete(`/customer/cart/items/${itemId}`)
    return response.data.data
  },

  clearCart: async () => {
    const response = await cartAxios.delete('/customer/cart/clear')
    return response.data
  },

  applyCoupon: async (couponCode) => {
    const response = await cartAxios.post('/customer/cart/apply-coupon', {
      couponCode,
      validateOnly: false,
    })
    return response.data.data
  },

  removeCoupon: async (couponCode) => {
    const response = await cartAxios.delete('/customer/cart/remove-coupon', {
      params: couponCode ? { couponCode } : {},
    })
    return response.data.data
  },

  getCartItemCount: async () => {
    const response = await cartAxios.get('/customer/cart/count')
    return response.data.data
  },

  mergeCart: async (guestCartId) => {
    const response = await cartAxios.post('/customer/cart/merge', { guestCartId })
    return response.data.data
  },

  batchUpdateCart: async (cartData) => {
    const response = await cartAxios.post('/customer/cart/batch', cartData)
    return response.data.data
  },

  validateCart: async (checkData) => {
    const response = await cartAxios.post('/customer/cart/validate', checkData)
    return response.data.data
  },

  estimateShipping: async (addressId, country, postalCode) => {
    const response = await cartAxios.get('/customer/cart/shipping-estimate', {
      params: { addressId, country, postalCode },
    })
    return response.data.data
  },

  estimateTax: async (addressId) => {
    const response = await cartAxios.get('/customer/cart/tax-estimate', {
      params: { addressId },
    })
    return response.data.data
  },

  saveCart: async (cartName) => {
    const response = await cartAxios.post('/customer/cart/save', null, {
      params: { cartName },
    })
    return response.data
  },

  getSavedCarts: async () => {
    const response = await cartAxios.get('/customer/cart/saved')
    return response.data.data
  },

  restoreCart: async (savedCartId) => {
    const response = await cartAxios.post(`/customer/cart/restore/${savedCartId}`)
    return response.data.data
  },

  recalculateCart: async () => {
    const response = await cartAxios.post('/customer/cart/recalculate')
    return response.data.data
  },

  getCartSummary: async () => {
    const response = await cartAxios.get('/customer/cart/summary')
    return response.data.data
  },
}